// The appearance preference, applied + persisted + synced (Brief 4 / P1).
// The light / dark / high-contrast axis on the P0a token spine, driving
// `[data-appearance]` on <html>. (The old South Seas theme system was removed
// in the P7 cutover; this is the one appearance system now.)

import { useCallback, useEffect, useRef, useState } from "react";
import type { Appearance } from "@waytide/core";
import {
  appearanceAttr,
  appearanceCookie,
  parseAppearanceCookie,
} from "@waytide/core";
import { getPreferences, putPreferences, subscribeChat } from "../api/client";
import { updateFavicon } from "./favicon";

/** Apply an appearance to <html> as FACT — set `[data-appearance]` for an
 * explicit value, remove it for `system` (so `prefers-color-scheme` decides).
 * The flip is an EFFECT (color), so it SNAPS: one attribute write, no
 * transition, no animation (the motion law for a live theme change). */
export function applyAppearance(a: Appearance): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const attr = appearanceAttr(a);
  if (attr) root.setAttribute("data-appearance", attr);
  else root.removeAttribute("data-appearance");
  updateFavicon();
}

/** The appearance the pre-paint already applied (from the `wt_appearance`
 * cookie), so the hook's first render matches the painted screen with no second
 * flip. */
function initialAppearance(): Appearance {
  if (typeof document === "undefined") return "system";
  return parseAppearanceCookie(document.cookie) ?? "system";
}

export interface UseAppearance {
  appearance: Appearance;
  setAppearance: (a: Appearance) => void;
}

/** The saved UI appearance, applied + persisted + synced. `setAppearance`
 * snaps `[data-appearance]`, writes the `wt_appearance` cookie (so reload has no
 * flash), and persists via `PUT /preferences`. On mount it reconciles with the
 * server (the saved value wins over a stale cookie) and listens for changes
 * from the user's OTHER surfaces over the `preferences` SSE kind. */
export function useAppearance(): UseAppearance {
  const [appearance, setState] = useState<Appearance>(initialAppearance);
  // The latest applied value, so the SSE handler skips our own echo.
  const current = useRef(appearance);

  const apply = useCallback((a: Appearance, persist: boolean) => {
    current.current = a;
    setState(a);
    applyAppearance(a);
    if (typeof document !== "undefined") document.cookie = appearanceCookie(a);
    if (persist) void putPreferences(a).catch(() => {});
  }, []);

  const setAppearance = useCallback((a: Appearance) => apply(a, true), [apply]);

  // Reconcile with the server on mount (the saved value wins over a stale
  // cookie). Best-effort — a blip leaves the pre-paint value in place.
  useEffect(() => {
    let alive = true;
    void getPreferences()
      .then((p) => {
        // Don't let the coarse server preference clobber an explicit washed-theme
        // pick living in the cookie (the theme family is client-side until the
        // backend learns the full id set).
        const cookieId = parseAppearanceCookie(document.cookie);
        const cookieIsTheme =
          !!cookieId && !["light", "dark", "high-contrast"].includes(cookieId);
        if (alive && !cookieIsTheme && p.appearance !== current.current)
          apply(p.appearance, false);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [apply]);

  // Cross-surface sync: a change on the user's other surface arrives over the
  // `preferences` SSE; apply it as a snap (skipping our own echo).
  useEffect(
    () =>
      subscribeChat(
        () => {},
        () => {},
        undefined,
        undefined,
        (p) => {
          const cookieId = parseAppearanceCookie(document.cookie);
          const cookieIsTheme =
            !!cookieId && !["light", "dark", "high-contrast"].includes(cookieId);
          if (!cookieIsTheme && p.appearance !== current.current)
            apply(p.appearance, false);
        },
      ),
    [apply],
  );

  return { appearance, setAppearance };
}
